import { useQuery } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { Event, Feature, PricingClient } from 'src/WebApiController';

import { useUserHasFeature } from './useUserHasFeature';

export const useGetEventAutoPricingSettings = (event: Event | null) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();

  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);
  const hasEventPricingTeamFeature = useUserHasFeature(
    Feature.EventPricingTeam
  );

  const shouldQuery = !(
    !activeAccountWebClientConfig.activeAccountId ||
    !event?.viagId ||
    !hasAutoPricingFeature
  );

  const pricingSettingsQuery = useQuery({
    queryKey: ['PricingClient.getEventAutoPricingSettings', event?.viagId],
    queryFn: async () => {
      if (!shouldQuery) {
        return null;
      }

      const pricingSettings = await new PricingClient(
        activeAccountWebClientConfig
      ).getEventAutoPricingSettings(event!.viagId!);

      return pricingSettings;
    },

    enabled: shouldQuery,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(500 ** attemptIndex, 3000),
    meta: {
      // TODO: temporally hide the error for now, enable this after https://stubhub.enterprise.slack.com/archives/C08D76X6M0S is fixed.
      // onError: (error: ErrorTypes) => {
      //   showErrorDialog('PricingClient.getEventAutoPricingSettings', error, {
      //     trackErrorData: { event },
      //   });
      // },
    },
  });

  const shouldQueryUserGroupId = shouldQuery && hasEventPricingTeamFeature;

  const userGroupIdQuery = useQuery({
    queryKey: ['PricingClient.GetSellerEventPricingUserGroupId', event?.viagId],
    queryFn: async () => {
      if (!shouldQueryUserGroupId) {
        return null;
      }

      const userGroupId = await new PricingClient(
        activeAccountWebClientConfig
      ).getSellerEventPricingUserGroupId(event!.viagId!);

      return userGroupId;
    },

    enabled: shouldQueryUserGroupId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(500 ** attemptIndex, 3000),
    meta: {
      onError: (error: ErrorTypes) => {
        showErrorDialog(
          'PricingClient.GetSellerEventPricingUserGroupId',
          error,
          {
            trackErrorData: { event },
          }
        );
      },
    },
  });

  return {
    pricingSettings: pricingSettingsQuery.data,
    userGroupId: userGroupIdQuery.data,
    pricingSettingsQuery,
    userGroupIdQuery,
    hasEventPricingTeamFeature,
  };
};
